import { logger } from "../../utils/logger";
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, LocalOffer as LocalOfferIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { Company } from '../../types/company';
import ProductTaxCodeDialog from '../../components/tax/ProductTaxCodeDialog';
import { companyService } from '../../services/companyService';
// companyService already imported above

const MotionPaper = motion(Paper);

export const CompanyList = () => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
    const [taxDialogOpen, setTaxDialogOpen] = useState(false);
    const [companyForTaxEdit, setCompanyForTaxEdit] = useState<Company | null>(null);

    const fetchCompanies = async (forceRefresh = false) => {
        try {
            setLoading(true);
            
            // If forceRefresh is true, first clear any caches
            if (forceRefresh) {
                setRefreshing(true);
                await companyService.refreshCompanyData();
            }
            
            const data = await companyService.listCompanies();
            setCompanies(data);
            setError(null);
            
            if (data.length === 0) {
                enqueueSnackbar('Please add your first company to get started.', { 
                    variant: 'info',
                    autoHideDuration: 5000
                });
            }
        } catch (err) {
            logger.error('Error loading companies:', err);
            setError('Failed to load companies. Please try again later.');
            enqueueSnackbar('Error loading companies', { variant: 'error' });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        enqueueSnackbar('Refreshing company data...', { variant: 'info' });
        await fetchCompanies(true);
        enqueueSnackbar('Company data refreshed successfully', { variant: 'success' });
    };

    useEffect(() => {
        // Load companies on component mount
        fetchCompanies(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeleteClick = (company: Company) => {
        setCompanyToDelete(company);
        setDeleteDialogOpen(true);
    };
    const handleDeleteConfirm = async () => {
        if (!companyToDelete) return;
        try {
            await companyService.deleteCompany(companyToDelete.id);
            setCompanies(companies.filter(c => c.id !== companyToDelete.id));
            enqueueSnackbar('Company deleted successfully', { variant: 'success', autoHideDuration: 3000 });
        } catch (err: any) {
            logger.error('Error deleting company:', err);
            if (err.response?.status === 403) {
                enqueueSnackbar('You do not have permission to delete this company', { variant: 'error', autoHideDuration: 5000 });
            } else {
                enqueueSnackbar('Failed to delete company: ' + (err.response?.data?.detail || 'Unknown error'), { variant: 'error', autoHideDuration: 5000 });
            }
        } finally {
            setDeleteDialogOpen(false);
            setCompanyToDelete(null);
        }
    };
    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
    };

    const openTaxDialog = (company: Company) => {
        setCompanyForTaxEdit(company);
        setTaxDialogOpen(true);
    };

    const closeTaxDialog = () => {
        setTaxDialogOpen(false);
        setCompanyForTaxEdit(null);
    };

    const handleTaxCodeSelect = async (code: string) => {
        if (!companyForTaxEdit) return;
        try {
            const updated = await companyService.updateCompany(companyForTaxEdit.id, { tax_code: code });
            setCompanies(prev => prev.map(c => (c.id === updated.id ? { ...c, tax_code: updated.tax_code } : c)));
            enqueueSnackbar('Default product tax code updated', { variant: 'success' });
        } catch (err: any) {
            logger.error('Failed to update company tax code', err);
            enqueueSnackbar('Failed to update product tax code', { variant: 'error' });
        } finally {
            closeTaxDialog();
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="gt-page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1" className="gt-title">Companies</Typography>
                    <Typography variant="body1" color="text.secondary" className="gt-desc">
                        Each company keeps its own transactions, nexus and tax codes.
                    </Typography>
                </Box>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={refreshing}
                        sx={{ mr: 2 }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/companies/new')}
                    >
                        Add Company
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <MotionPaper
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Company Name</TableCell>
                                <TableCell>Tax ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Head Office</TableCell>
                                <TableCell>Product Tax Code</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {companies.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography variant="body1" sx={{ py: 2 }}>
                                            No companies found. Add your first company to get started.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                companies.map((company) => (
                                    <TableRow key={company.id}>
                                        <TableCell>{company.name}</TableCell>
                                        <TableCell>{company.tax_id}</TableCell>
                                        <TableCell>{company.is_active ? 'Active' : 'Inactive'}</TableCell>
                                        <TableCell>
                                            {company.is_head_office && (
                                                <Box 
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1,
                                                        py: 0.5,
                                                        bgcolor: 'primary.main',
                                                        color: 'white',
                                                        borderRadius: 1,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    YES
                                                </Box>
                                            )}
                                         </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {company.tax_code ? (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-block',
                                                            px: 1,
                                                            py: 0.5,
                                                            bgcolor: 'secondary.light',
                                                            color: 'secondary.contrastText',
                                                            borderRadius: 1,
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                            maxWidth: 200,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                        title={company.tax_code}
                                                    >
                                                        {company.tax_code}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">Not set</Typography>
                                                )}
                                                <IconButton size="small" color="primary" onClick={() => openTaxDialog(company)} title={company.tax_code ? 'Change tax code' : 'Set tax code'}>
                                                    <LocalOfferIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={() => navigate(`/companies/${company.id}/edit`)}
                                                color="primary"
                                                size="small"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => handleDeleteClick(company)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MotionPaper>
            <ProductTaxCodeDialog
                open={taxDialogOpen}
                onClose={closeTaxDialog}
                onSelect={handleTaxCodeSelect}
            />
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Company</DialogTitle>
                <DialogContent>
                    <Typography color="error" fontWeight="bold">
                        Warning: This will permanently delete the company and all associated data (tax records, transactions, reports, etc). This action cannot be undone.
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        Are you sure you want to delete <b>{companyToDelete?.name}</b>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="inherit">Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}; 